<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    :inputs="inputs"
    @save="save"
  >
    <template #partitionsLayerId.input="data">
      <v-col cols="3">
        <data-input
          v-model="selectedDataSources.unassigned_watermeters_partitions_datasource.settings['layer_id']"
          v-bind="data"
          @change="partitionsLayerIdChanged"
        />
      </v-col>
    </template>
    <template #watermetersLayerId.input="data">
      <v-col cols="3">
        <data-input
          v-model="selectedDataSources.unassigned_watermeters_datasource.settings['layer_id']"
          v-bind="data"
          @change="watermetersLayerIdChanged"
        />
      </v-col>
    </template>
    <template #aquagisAttributes.input="data">
      <v-row no-gutters>
        <v-col class="pa-0 text-subtitle-1 font-weight-bold" cols="auto">
          {{ $i18n.t(data.translationPath) }}
        </v-col>
      </v-row>
    </template>
    <template #watermetersLayerBillingIdAttribute.input="data">
      <v-col cols="3">
        <data-input
          v-model="selectedDataSources.unassigned_watermeters_datasource.settings['billing_id_attribute']"
          v-bind="data"
          @change="selectedDataSources.unassigned_watermeters_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #watermetersLayerPartitionIdAttribute.input="data">
      <v-col cols="3">
        <data-input
          v-model="selectedDataSources.unassigned_watermeters_datasource.settings['parent_partition_attribute']"
          v-bind="data"
          @change="selectedDataSources.unassigned_watermeters_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #billingsAttributes.input="data">
      <v-row no-gutters>
        <v-col class="pa-0 text-subtitle-1 font-weight-bold" cols="auto">
          {{ $i18n.t(data.translationPath) }}
        </v-col>
      </v-row>
    </template>
    <template #watermetersLayerDisplayId.input="data">
      <v-col cols="3">
        <data-input
          v-model="selectedDataSources.unassigned_watermeters_datasource.settings['display_id_attribute']"
          v-bind="data"
          @change="selectedDataSources.unassigned_watermeters_datasource.changed = true"
        />
      </v-col>
    </template>
    <template #watermetersLayerDisplayAttributes.input="data">
      <v-col cols="3">
        <data-input
          v-model="selectedDataSources.unassigned_watermeters_datasource.settings['display_attributes']"
          v-bind="data"
          @change="selectedDataSources.unassigned_watermeters_datasource.changed = true"
        />
      </v-col>
    </template>
  </admin-wrapper>
</template>
<script>
import { call, get } from 'vuex-pathify';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

export default {
  name: 'UnassignedWatermetersSettings',
  components: { AdminWrapper, DataInput },
  mixins: [prepareAttributesFeatureCardMixin],
  computed: {
    additionalModuleName: get('admin/additionalModules@UNASSIGNED_WATERMETERS_MODULE.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@unassigned_watermeters_module_enabled'),
    projects: get('layers/projects'),
    additionalDataSources: get('layers/additionalMetadata'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers);
    },
    layersWaterArray() {
      return this.layersArray.filter(
        layer =>
          layer.type === 'features_layer' && layer.geometry_type?.includes('point') && layer.layer_scope === 'water'
      );
    },
    layersWaterMapped() {
      return this.layersWaterArray.map(layer => {
        const { id, name } = layer;
        return { value: id, text: name };
      });
    },
    projectsMapped() {
      return Object.values(this.projects)
        .filter(project => project.role === 'predefined')
        .map(project => {
          const { id, name } = project;
          return { text: name, value: id };
        });
    },
    inputs() {
      return [
        {
          name: 'unassigned_watermeters_project',
          translationPath: 'admin.unassignedWatermeters.moduleProject',
          subtitle: 'admin.unassignedWatermeters.moduleProjectSubtitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.projectsMapped,
        },
        {
          name: 'partitionsLayerId',
          translationPath: 'admin.unassignedWatermeters.partitionsLayer',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.layersWaterMapped,
        },
        {
          name: 'watermetersLayerId',
          translationPath: 'admin.unassignedWatermeters.watermetersLayer',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.layersWaterMapped,
        },
        {
          name: 'aquagisAttributes',
          translationPath: 'admin.unassignedWatermeters.aquagisAttributes',
          isNamedSlot: false,
        },
        {
          name: 'watermetersLayerBillingIdAttribute',
          translationPath: 'admin.unassignedWatermeters.watermetersLayerBillingIdAttribute',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedWatermetersLayerMappedAttributes,
          disabled: !this.selectedWatermetersLayerDataSourceName,
        },
        {
          name: 'watermetersLayerPartitionIdAttribute',
          translationPath: 'admin.unassignedWatermeters.watermetersLayerPartitionIdAttribute',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedWatermetersLayerMappedAttributes,
          disabled: !this.selectedWatermetersLayerDataSourceName,
        },
        {
          name: 'billingsAttributes',
          translationPath: 'admin.unassignedWatermeters.billingsAttributes',
          isNamedSlot: false,
        },
        {
          name: 'watermetersLayerDisplayId',
          translationPath: 'admin.unassignedWatermeters.watermetersLayerDisplayId',
          subtitle: 'admin.unassignedWatermeters.watermetersLayerDisplayIdSubtitle',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedWatermetersLayerMappedAttributes,
          disabled: !this.selectedWatermetersLayerDataSourceName,
        },
        {
          name: 'watermetersLayerDisplayAttributes',
          translationPath: 'admin.unassignedWatermeters.watermetersLayerDisplayAttributes',
          subtitle: 'admin.unassignedWatermeters.watermetersLayerDisplayAttributesSubtitle',
          rules: [v => !!v || v.length > 0 || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedWatermetersLayerMappedAttributes,
          disabled: !this.selectedWatermetersLayerDataSourceName,
          multiple: true,
          autoConvertSelect: false,
        },
      ];
    },
    moduleEnabler() {
      return {
        name: 'unassigned_watermeters_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },

    selectedPartitionsLayerMetadata() {
      return (
        this.layers?.[this.selectedDataSources.unassigned_watermeters_partitions_datasource.settings.layer_id] ||
        undefined
      );
    },
    selectedPartitionsLayerDataSourceName() {
      return this.selectedPartitionsLayerMetadata?.data_source_name || '';
    },

    selectedWatermetersLayerMetadata() {
      return this.layers?.[this.selectedDataSources.unassigned_watermeters_datasource.settings.layer_id] || undefined;
    },
    selectedWatermetersLayerFormSchema() {
      return this.selectedWatermetersLayerMetadata?.form_schema?.elements || [];
    },
    selectedWatermetersLayerDataSourceName() {
      return this.selectedWatermetersLayerMetadata?.data_source_name || '';
    },
    selectedWatermetersLayerDataSourceMetadata() {
      return this.dataSources?.[this.selectedWatermetersLayerDataSourceName] || undefined;
    },
    selectedWatermetersLayerDataSourceAttributesSchema() {
      return this.selectedWatermetersLayerDataSourceMetadata?.attributes_schema?.attributes || [];
    },
    selectedWatermetersLayerAttributes() {
      return this.$_flattenStructure(
        this.getForm(this.selectedWatermetersLayerFormSchema, this.selectedWatermetersLayerDataSourceAttributesSchema, {
          returnDictRelationInfo: true,
        })
      );
    },
    selectedWatermetersLayerMappedAttributes() {
      return this.selectedWatermetersLayerAttributes.map(attribute => {
        const { attribute: value, label } = attribute;
        return { value, text: label };
      });
    },
  },
  data: () => ({
    isFormFetched: false,
    selectedDataSources: {
      unassigned_watermeters_datasource: {
        changed: false,
        datasource: '',
        settings: {
          layer_id: null,
          display_id_attribute: '',
          display_attributes: [],
          parent_partition_attribute: '',
          billing_id_attribute: '',
        },
      },
      unassigned_watermeters_partitions_datasource: {
        changed: false,
        datasource: '',
        settings: {
          layer_id: null,
        },
      },
    },
  }),
  methods: {
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    getDataSources: call('layers/getLayersMetadata'),
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    getLayers: call('layers/getLayers'),
    getModulesMapping: call('admin/getModulesMapping'),
    getProjects: call('layers/getProjects'),
    async init() {
      await Promise.all([
        this.getModulesMapping(),
        this.getDataSources({ with_attributes_schema: true, withAdditionalMetadata: true }),
        this.getLayers(),
        this.getProjects({ with_default: true }),
      ]);
      if (this.isModuleEnabled) {
        await this.updateSelectedDataSource();
      }
      this.isFormFetched = true;
    },
    async updateSelectedDataSource() {
      try {
        const selectedDataSourcesKeys = Object.keys(this.selectedDataSources);
        const promises = selectedDataSourcesKeys.map(selectedDataSourcesKey => {
          return this.getDatasourceSettings(selectedDataSourcesKey);
        });
        const rPromisesData = await Promise.all(promises);
        this.selectedDataSources = rPromisesData.reduce(
          (total, current, index) => ({
            ...total,
            ...{
              [selectedDataSourcesKeys[index]]: {
                datasource: current.datasource,
                settings: current.settings,
                changed: false,
              },
            },
          }),
          {}
        );
      } catch {
        //
      }
    },
    async save() {
      try {
        const selectedDataSourcesKeys = Object.keys(this.selectedDataSources);
        const filteredSelectedDataSourcesKeys = selectedDataSourcesKeys.filter(
          key => this.selectedDataSources[key].changed
        );
        const promises = filteredSelectedDataSourcesKeys.map(selectedDataSourcesKey => {
          const settings = JSON.parse(JSON.stringify(this.selectedDataSources[selectedDataSourcesKey]));
          delete settings.changed;
          const payload = { name: selectedDataSourcesKey, settings };
          return this.editDatasourceSettings(payload);
        });
        await Promise.all(promises);
        this.selectedDataSources = selectedDataSourcesKeys.reduce(
          (total, current) => ({ ...total, [current]: { ...this.selectedDataSources[current], changed: false } }),
          {}
        );
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch {
        //
      }
    },
    partitionsLayerIdChanged(value) {
      this.selectedDataSources.unassigned_watermeters_partitions_datasource.changed = true;
      if (!value && value !== 0) return;
      this.selectedDataSources.unassigned_watermeters_partitions_datasource.datasource =
        this.selectedPartitionsLayerDataSourceName || '';
    },
    watermetersLayerIdChanged(value) {
      this.selectedDataSources.unassigned_watermeters_datasource.settings = {
        layer_id: this.selectedDataSources.unassigned_watermeters_datasource.settings.layer_id,
        display_id_attribute: '',
        display_attributes: [],
        parent_partition_attribute: '',
        billing_id_attribute: '',
      };
      this.selectedDataSources.unassigned_watermeters_datasource.changed = true;
      if (!value && value !== 0) return;
      this.selectedDataSources.unassigned_watermeters_datasource.datasource =
        this.selectedWatermetersLayerDataSourceName || '';
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
